import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useAdminAuth } from '../context/AdminAuthContext'

interface FormData {
  email: string
  password: string
}

export function AdminLogin() {
  const { login, isAdminAuthenticated } = useAdminAuth()
  const navigate = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()

  // Se già autenticato, redirect a /admin
  if (isAdminAuthenticated) {
    navigate('/admin', { replace: true })
    return null
  }

  async function onSubmit(data: FormData) {
    setServerError(null)
    const err = await login(data.email, data.password)
    if (err) {
      setServerError(err)
    } else {
      navigate('/admin', { replace: true })
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'var(--km-bg-deep)' }}
    >
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="flex flex-col items-center mb-8 gap-2">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold text-white"
            style={{ background: 'var(--km-gradient-brand)' }}
          >
            K
          </div>
          <div className="text-center">
            <div className="text-base font-bold text-white tracking-widest">KOREMARGIN</div>
            <div className="text-xs font-semibold tracking-widest mt-0.5" style={{ color: 'var(--km-primary)' }}>
              ADMIN PANEL
            </div>
          </div>
        </div>

        {/* Form card */}
        <div
          className="rounded-xl border p-6"
          style={{
            background: 'var(--km-surface)',
            borderColor: 'var(--km-border)',
          }}
        >
          <h2 className="text-base font-semibold mb-5" style={{ color: 'var(--km-text-primary)' }}>
            Accesso riservato al team Koremargin
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            {/* Email */}
            <div>
              <label
                htmlFor="admin-email"
                className="block text-xs font-medium mb-1.5"
                style={{ color: 'var(--km-text-secondary)' }}
              >
                Email
              </label>
              <input
                id="admin-email"
                type="email"
                autoComplete="email"
                className="w-full px-3 py-2.5 rounded-lg text-sm border outline-none transition-colors"
                style={{
                  background: 'var(--km-bg)',
                  borderColor: errors.email ? 'var(--km-error)' : 'var(--km-border)',
                  color: 'var(--km-text-primary)',
                }}
                {...register('email', {
                  required: 'Email obbligatoria',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Email non valida',
                  },
                })}
              />
              {errors.email && (
                <p className="mt-1 text-xs" style={{ color: 'var(--km-error)' }}>
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="admin-password"
                className="block text-xs font-medium mb-1.5"
                style={{ color: 'var(--km-text-secondary)' }}
              >
                Password
              </label>
              <input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                className="w-full px-3 py-2.5 rounded-lg text-sm border outline-none transition-colors"
                style={{
                  background: 'var(--km-bg)',
                  borderColor: errors.password ? 'var(--km-error)' : 'var(--km-border)',
                  color: 'var(--km-text-primary)',
                }}
                {...register('password', { required: 'Password obbligatoria' })}
              />
              {errors.password && (
                <p className="mt-1 text-xs" style={{ color: 'var(--km-error)' }}>
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Server error */}
            {serverError && (
              <div
                className="rounded-lg px-3 py-2.5 text-sm"
                style={{ background: 'rgba(239,68,68,0.12)', color: 'var(--km-error)' }}
              >
                {serverError}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-60"
              style={{ background: 'var(--km-gradient-brand)' }}
            >
              {isSubmitting ? 'Accesso in corso...' : 'Accedi'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
